<div class="kt-portlet">
	<kt-portlet-header [sticky]="false" [title]="'Auger Purge'" [class]="'kt-portlet__head--lg'"></kt-portlet-header>
	<div class="kt-portlet__body">
		<div class="auger-purge col-xl-10 col-md-12 col-sm-12 mt-3 mx-auto">
			<mat-progress-bar *ngIf="isLoading || phase === 'sending'" mode="indeterminate"></mat-progress-bar>

			<div class="auger-purge__error" role="alert" *ngIf="loadError">{{ loadError }}</div>
			<div class="auger-purge__error" role="alert" *ngIf="formError">{{ formError }}</div>

			<div class="auger-purge__status auger-purge__status--pending" role="status" *ngIf="phase === 'waiting'">
				<mat-icon aria-hidden="true">schedule</mat-icon>
				<div class="auger-purge__status-text">
					<p class="auger-purge__title">{{ statusMessage }}</p>
					<p>The machine applies the change and reports back. This usually takes a few seconds.</p>
				</div>
			</div>

			<div class="auger-purge__status auger-purge__status--applied" role="status" *ngIf="phase === 'applied'">
				<mat-icon aria-hidden="true">check_circle</mat-icon>
				<div class="auger-purge__status-text">
					<p class="auger-purge__title">{{ statusMessage }}</p>
					<p>The settings below are what the machine reported.</p>
				</div>
			</div>

			<div class="auger-purge__status auger-purge__status--pending" role="status" *ngIf="phase === 'not_confirmed'">
				<mat-icon aria-hidden="true">error_outline</mat-icon>
				<div class="auger-purge__status-text">
					<p class="auger-purge__title">{{ statusMessage }}</p>
					<p>The change was sent once. Send it again if the machine is back online.</p>
				</div>
				<button mat-stroked-button type="button" (click)="sendAgain()" [disabled]="formLocked">Send again</button>
			</div>

			<div class="auger-purge__error" role="alert" *ngIf="phase === 'failed'">{{ statusMessage }}</div>

			<ng-container *ngIf="!isLoading && config && draft">
				<section class="auger-purge__section" aria-labelledby="auger-purge-machine-heading">
					<h3 id="auger-purge-machine-heading" class="auger-purge__section-title">Machine</h3>
					<p class="auger-purge__muted">
						Before a cleaning cycle, the machine can briefly run a canister's auger to clear powder that has been sitting in it.
						Default means no change has been reported, so the machine is using its starting value.
					</p>

					<div class="auger-purge__cards">
						<div class="auger-purge__card">
							<span class="auger-purge__context-label">Purge</span>
							<kt-segmented-control
								[options]="purgeOptions"
								[value]="draft.enabled"
								[disabled]="formLocked"
								(valueChange)="onEnabledChange($event)">
							</kt-segmented-control>
							<p class="auger-purge__muted">Off means no canister purges. Reported: {{ enabledLabel() }}.</p>
							<p class="auger-purge__field-error" *ngIf="enabledError()">{{ enabledError() }}</p>
						</div>
						<div class="auger-purge__card">
							<span class="auger-purge__context-label">Cleaning basis</span>
							<span class="auger-purge__name" *ngIf="config.cleaning_basis">{{ basisLabel() }}</span>
							<span class="auger-purge__pill auger-purge__pill--default" *ngIf="!config.cleaning_basis" [matTooltip]="basisDefaultTooltip">Default</span>
							<p class="auger-purge__muted">A cleaning cycle is based on drink count or on time, in minutes. Set on the machine by a technician. Not editable here.</p>
						</div>
					</div>
				</section>

				<section class="auger-purge__section" aria-labelledby="auger-purge-canisters-heading">
					<h3 id="auger-purge-canisters-heading" class="auger-purge__section-title">Canisters</h3>
					<p class="auger-purge__note">
						<mat-icon aria-hidden="true">info_outline</mat-icon>
						<span>Cleaning interval is one machine setting, reported on every canister. Set on the machine by a technician. Not editable here. Frequency is how many of those cycles pass before a purge. 0 turns purging off for that canister. With a drinks basis, an interval of 40 and a frequency of 2 means the canister purges every 80th drink.</span>
					</p>

					<div class="auger-purge__table-wrap">
						<table class="auger-purge__table">
							<thead>
								<tr>
									<th>Canister</th>
									<th>Cleaning interval</th>
									<th *ngFor="let field of fields">{{ field.label }}</th>
								</tr>
							</thead>
							<tbody>
								<tr *ngFor="let row of draft.canisters">
									<td>
										<div class="auger-purge__canister">
											<span class="auger-purge__slot">{{ row.slot }}</span>
											<span class="auger-purge__product">{{ productName(row.slot) || 'Canister ' + row.slot }}</span>
										</div>
									</td>
									<td>
										<span *ngIf="intervalText(row.slot)">{{ intervalText(row.slot) }}</span>
										<span class="auger-purge__pill auger-purge__pill--default" *ngIf="!intervalText(row.slot)" [matTooltip]="intervalDefaultTooltip">Default</span>
									</td>
									<td *ngFor="let field of fields">
										<div class="auger-purge__field">
											<div class="auger-purge__stepper" [class.auger-purge__stepper--error]="!!fieldError(row.slot, field.key)">
												<button
													type="button"
													class="auger-purge__step"
													[attr.aria-label]="'Decrease ' + field.label"
													[disabled]="formLocked || !canStep(row, field, -1)"
													(mousedown)="$event.preventDefault()"
													(click)="stepField(row, field, -1)">−</button>
												<div class="auger-purge__step-value">
													<input
														type="text"
														inputmode="numeric"
														autocomplete="off"
														[ngStyle]="{ width: inputWidth(row, field) }"
														[value]="row[field.key]"
														(input)="onFieldInput(row, field.key, $event)"
														[disabled]="formLocked"
														[placeholder]="unknownField(row.slot, field.key) ? field.unreportedLabel : ''"
														[matTooltip]="fieldTooltip(row, field.key)"
														[matTooltipDisabled]="!fieldTooltip(row, field.key)"
														[attr.aria-label]="field.label">
													<span class="auger-purge__unit" *ngIf="field.unit && row[field.key]">{{ field.unit }}</span>
													<span class="auger-purge__off" *ngIf="field.key === 'frequency_cycles' && isFrequencyOff(row)">Off</span>
												</div>
												<button
													type="button"
													class="auger-purge__step"
													[attr.aria-label]="'Increase ' + field.label"
													[disabled]="formLocked || !canStep(row, field, 1)"
													(mousedown)="$event.preventDefault()"
													(click)="stepField(row, field, 1)">+</button>
											</div>
											<p class="auger-purge__field-error" *ngIf="fieldError(row.slot, field.key)">{{ fieldError(row.slot, field.key) }}</p>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div class="auger-purge__actions">
						<button mat-raised-button color="primary" type="button" (click)="onSend()" [disabled]="!canSend">Send</button>
					</div>
				</section>

				<section class="auger-purge__section" aria-labelledby="auger-purge-history-heading">
					<h3 id="auger-purge-history-heading" class="auger-purge__section-title">Recent changes</h3>
					<p class="auger-purge__muted" *ngIf="!config.recent_commands || !config.recent_commands.length">No changes yet.</p>
					<div class="auger-purge__table-wrap" *ngIf="config.recent_commands && config.recent_commands.length">
						<table class="auger-purge__table">
							<thead>
								<tr>
									<th>When</th>
									<th>Who</th>
									<th>Change</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								<tr *ngFor="let command of config.recent_commands">
									<td>{{ (command.created_at || command.sent_at) ? ((command.created_at || command.sent_at) | date:'MM/dd/yy hh:mm a') : '—' }}</td>
									<td>{{ command.requested_by || '—' }}</td>
									<td class="auger-purge__change">{{ describeChange(command.payload) }}</td>
									<td>
										<span class="auger-purge__pill"
											[class.auger-purge__pill--on]="command.status === 'acked'"
											[class.auger-purge__pill--bad]="command.status === 'failed'">
											{{ statusLabel(command.status) }}
										</span>
										<p class="auger-purge__muted" *ngIf="command.status === 'failed' && command.error">{{ command.error }}</p>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>
			</ng-container>
		</div>
	</div>
</div>
